import { __decorate, __param } from '../../../../../../../external/tslib/tslib.es6.js';
import { CancellationToken } from 'monaco-editor/esm/vs/base/common/cancellation.js';
import { Emitter } from 'monaco-editor/esm/vs/base/common/event.js';
import { toDisposable, Disposable } from 'monaco-editor/esm/vs/base/common/lifecycle.js';
import { Schemas } from 'monaco-editor/esm/vs/base/common/network.js';
import { URI } from 'monaco-editor/esm/vs/base/common/uri.js';
import * as nls from 'monaco-editor/esm/vs/nls.js';
import { EditorInput } from '../../../common/editor/editorInput.js';
import { IChatService } from '../common/chatService.js';
var ChatEditorInput_1;
let ChatEditorInput = class ChatEditorInput extends EditorInput {
    static { ChatEditorInput_1 = this; }
    static { this.countsInUse = ( new Set()); }
    static { this.TypeID = 'workbench.input.chatSession'; }
    static { this.EditorID = 'workbench.editor.chatSession'; }
    static getNewEditorUri() {
        const handle = Math.floor(Math.random() * 1e9);
        return ChatUri.generate(handle);
    }
    static getNextCount() {
        let count = 0;
        while (( ChatEditorInput_1.countsInUse.has(count))) {
            count++;
        }
        return count;
    }
    constructor(resource, options, chatService) {
        super();
        this.resource = resource;
        this.options = options;
        this.chatService = chatService;
        const parsed = ChatUri.parse(resource);
        if (typeof parsed?.handle !== 'number') {
            throw new Error('Invalid chat URI');
        }
        this.sessionId = 'sessionId' in options.target ? options.target.sessionId : undefined;
        this.providerId = 'providerId' in options.target ? options.target.providerId : undefined;
        this.inputCount = ChatEditorInput_1.getNextCount();
        ChatEditorInput_1.countsInUse.add(this.inputCount);
        this._register(toDisposable(() => ChatEditorInput_1.countsInUse.delete(this.inputCount)));
    }
    get editorId() {
        return ChatEditorInput_1.EditorID;
    }
    get capabilities() {
        return super.capabilities | 8 ;
    }
    matches(otherInput) {
        return otherInput instanceof ChatEditorInput_1 && ( otherInput.resource.toString()) === ( this.resource.toString());
    }
    get typeId() {
        return ChatEditorInput_1.TypeID;
    }
    getName() {
        return this.model?.title || ( nls.localizeWithPath(
            'vs/workbench/contrib/chat/browser/chatEditorInput',
            'chatEditorName',
            "Chat"
        )) + (this.inputCount > 0 ? ` ${this.inputCount + 1}` : '');
    }
    getLabelExtraClasses() {
        return ['chat-editor-label'];
    }
    async resolve() {
        if (typeof this.sessionId === 'string') {
            this.model = this.chatService.getOrRestoreSession(this.sessionId);
        }
        else if (typeof this.providerId === 'string') {
            this.model = this.chatService.startSession(this.providerId, CancellationToken.None);
        }
        else if ('data' in this.options.target) {
            this.model = this.chatService.loadSessionFromContent(this.options.target.data);
        }
        if (!this.model) {
            return null;
        }
        this.sessionId = this.model.sessionId;
        this.providerId = this.model.providerId;
        this._register(this.model.onDidChange(() => this._onDidChangeLabel.fire()));
        return this._register(( new ChatEditorModel(this.model)));
    }
    dispose() {
        super.dispose();
        if (this.sessionId) {
            this.chatService.clearSession(this.sessionId);
        }
    }
};
ChatEditorInput = ChatEditorInput_1 = ( __decorate([
    ( __param(2, IChatService))
], ChatEditorInput));
class ChatEditorModel extends Disposable {
    constructor(model) {
        super();
        this.model = model;
        this._onWillDispose = this._register(( new Emitter()));
        this.onWillDispose = this._onWillDispose.event;
        this._isDisposed = false;
        this._isResolved = false;
    }
    async resolve() {
        this._isResolved = true;
    }
    isResolved() {
        return this._isResolved;
    }
    isDisposed() {
        return this._isDisposed;
    }
    dispose() {
        super.dispose();
        this._isDisposed = true;
    }
}
var ChatUri;
( (function(ChatUri) {
    ChatUri.scheme = Schemas.vscodeChatSesssion;
    function generate(handle) {
        return ( URI.from({ scheme: ChatUri.scheme, path: `chat-${handle}` }));
    }
    ChatUri.generate = generate;
    function parse(resource) {
        if (resource.scheme !== ChatUri.scheme) {
            return undefined;
        }
        const match = resource.path.match(/chat-(\d+)/);
        const handleStr = match?.[1];
        if (typeof handleStr !== 'string') {
            return undefined;
        }
        const handle = parseInt(handleStr);
        if (isNaN(handle)) {
            return undefined;
        }
        return { handle };
    }
    ChatUri.parse = parse;
})(ChatUri || (ChatUri = {})));
class ChatEditorInputSerializer {
    canSerialize(input) {
        return input instanceof ChatEditorInput;
    }
    serialize(input) {
        if (!(input instanceof ChatEditorInput)) {
            return undefined;
        }
        if (typeof input.sessionId !== 'string') {
            return undefined;
        }
        const obj = {
            options: input.options,
            sessionId: input.sessionId,
            resource: input.resource
        };
        return JSON.stringify(obj);
    }
    deserialize(instantiationService, serializedEditor) {
        try {
            const parsed = JSON.parse(serializedEditor);
            const resource = URI.revive(parsed.resource);
            return instantiationService.createInstance(ChatEditorInput, resource, { ...parsed.options, target: { sessionId: parsed.sessionId } });
        }
        catch (err) {
            return undefined;
        }
    }
}
export { ChatEditorInput, ChatEditorInputSerializer, ChatEditorModel, ChatUri };
